<template>
  <vee-field name="amount" v-model.number="amount">
    <base-form-field
      v-model="amount"
      :schema="schema.amount"
      :compare="{
        maximum: programMaximumLoanAmount,
        minimum: programMinimumLoanAmount,
        minimumDisplay: programMinimumLoanAmountDisplay,
      }"
			:disabled="disabled"
    >
      <template #errors><error-message class="error-message" name="amount" /></template>
    </base-form-field>
  </vee-field>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { Field as VeeField, useForm, ErrorMessage } from "vee-validate";
import * as yup from "yup";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import { useStore } from "vuex";
import type { FormSchema, FieldSchema } from "@/models/form";

const store = useStore();

const props = withDefaults(defineProps<{disabled: boolean}>(), {
	disabled: false,
});

const amount = ref<string | number | undefined>(undefined);

const showField = ref<boolean>(false);

const programMaximumLoanAmount = computed(() => store.state.appData?.application?.loanAmount);

const programMinimumLoanAmount = computed(
  () => store.getters["PROGRAM_MINIMUM_INITIAL_DRAW_AMOUNT"],
);

const programMinimumLoanAmountDisplay = computed(
  () => store.getters["PROGRAM_MINIMUM_INITIAL_DRAW_AMOUNT_DISPLAY"],
);

const minimumNotMetError = computed<string>(() => {
  return `Your request for funds must be greater than ${programMinimumLoanAmountDisplay.value}. Please enter an amount greater than or equal to ${programMinimumLoanAmountDisplay.value}.`;
});

const validationSchema = computed(() => {
  const schema = yup.object({
    amount: yup
      .number()
      .transform(value => (isNaN(value) ? undefined : value))
      .required("Amount is required")
      .max(
        programMaximumLoanAmount.value,
        "The amount requested is more than the total line of credit you've been approved for.",
      )
      .test(
        "Amount is valid",
        `${minimumNotMetError.value} You may also enter $0 if you are unsure.`,
        function (value) {
          const { path, createError } = this;
          if (value === null || value === undefined) {
            return true; // Handled by .required()
          }

          if (value < programMinimumLoanAmount.value && value !== 0) {
            return createError({
              path,
              message: `${minimumNotMetError.value} You may also enter $0 if you are unsure.`,
            });
          }
          return true;
        },
      ),
  });
  return showField.value ? schema : yup.object({});
});

const { values, errors, setValues } = useForm({
  validationSchema: validationSchema,
});

const hasErrors = computed(() => Object.keys(errors.value).length > 0);

let schema = ref<FormSchema>({
  amount: {
    name: "amount",
    type: "money",
    subtype: "initialDrawAmount",
    hideLabel: true,
    required: true,
    allowZero: true,
    allowDecimals: false,
    errorMessage: "An amount to borrow is required.",
    errors: [] as string[],
    minimum: programMinimumLoanAmount.value,
    productTypeId: store.state.appData?.application?.productTypeId,
    validate: (schema: FieldSchema) => {
      schema.valid = !hasErrors.value;
      schema.errors = errors.value.amount ? [errors.value.amount] : [];
    },
  },
});

function updateAmount(newAmount: number | string | undefined, shouldValidate = true) {
  amount.value = newAmount;

  if (newAmount === "") {
    amount.value = undefined;
  } else {
    amount.value = newAmount;
  }

  if (isNaN(Number(newAmount))) {
    setValues({ amount: undefined }, shouldValidate);
    return;
  }

  setValues({ amount: Number(amount.value) }, shouldValidate);
}

// Watchers
watch(
  () => amount.value,
  (newValue, oldValue) => {
    if (newValue === oldValue) {
      return;
    }

    updateAmount(newValue);
  },
);

onMounted(() => {
  showField.value = true;
});

onBeforeUnmount(() => {
  showField.value = false;
});

defineExpose({
	props,
  validationSchema,
  values,
  amount,
  setValues,
  hasErrors,
  updateAmount,
});
</script>

<style scoped>
.error-message {
  color: #b40000;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}
</style>
